import { useEffect, useRef, useState } from "react";
import { Transition } from "@headlessui/react";
import { twMerge } from "tailwind-merge";
import { useStore } from "../../store";
import { ReactComponent as Close } from "../../assets/icons/cross.svg";
import { ReactComponent as Success } from "../../assets/icons/check-circle-2.svg";
import { ReactComponent as Info } from "../../assets/icons/info.svg";
import { ReactComponent as Warning } from "../../assets/icons/alert-circle.svg";
import { ReactComponent as Error } from "../../assets/icons/cross-circle.svg";
import Typography from "../Typography";

export const Types = {
  info: "info",
  success: "success",
  warning: "warning",
  error: "error",
};

const DEFAULT_TITLES = {
  [Types.info]: "Анхаарна уу",
  [Types.success]: "Амжилттай",
  [Types.warning]: "Анхаарна уу",
  [Types.error]: "Алдаа гарлаа",
};

const ICON_WRAP = {
  [Types.info]: "bg-info-soft",
  [Types.success]: "bg-success-soft",
  [Types.warning]: "bg-warning-soft",
  [Types.error]: "bg-error-soft",
};

// Queues incoming notifications instead of letting a second one silently
// replace whatever is currently showing — each still gets its own turn on
// screen. `notification` is the raw store slot (one at a time, set via
// setNotification / useStoreBase.setState({ notification })); this component
// drains it into a local queue so nothing gets dropped.
const Notification = ({ notification }) => {
  const [queue, setQueue] = useState([]);
  const clearStoreNotification = useStore.useSetNotification();
  const nextId = useRef(0);

  useEffect(() => {
    if (!notification) return;
    nextId.current += 1;
    setQueue((q) => [
      ...q,
      { type: Types.error, ...notification, id: nextId.current },
    ]);
    clearStoreNotification(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  const current = queue[0];

  useEffect(() => {
    if (!current) return undefined;
    const timer = setTimeout(() => {
      setQueue((q) => q.slice(1));
    }, 5000);
    return () => clearTimeout(timer);
  }, [current]);

  const dismiss = () => setQueue((q) => q.slice(1));

  return (
    <div className="absolute inset-x-4 top-4 z-50">
      <Transition
        show={!!current}
        enter="transition-all duration-200 ease-out"
        enterFrom="opacity-0 -translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition-all duration-150 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-2"
      >
        <div
          role="alert"
          className="relative w-full px-4 py-4 overflow-hidden break-words bg-white rounded-card shadow-navigation"
        >
          {current && (
            <div className="box-border relative flex space-x-3">
              <div
                className={twMerge([
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  ICON_WRAP[current.type],
                ])}
              >
                {current.type === Types.info && <Info className="h-[18px] w-[18px]" />}
                {current.type === Types.success && (
                  <Success stroke="#0E8C4A" className="h-[18px] w-[18px]" />
                )}
                {current.type === Types.warning && (
                  <Warning stroke="#E3A13A" className="h-[18px] w-[18px]" />
                )}
                {current.type === Types.error && (
                  <Error stroke="#B7281E" className="h-[18px] w-[18px]" />
                )}
              </div>
              <div className="space-y-1 flex-1 min-w-0 text-primary pr-6">
                <div className="flex items-center gap-2">
                  <Typography variant="Title" size="sm">
                    {current.title || DEFAULT_TITLES[current.type]}
                  </Typography>
                  {queue.length > 1 && (
                    <Typography size="sm" className="text-black-64 tabular-nums">
                      +{queue.length - 1}
                    </Typography>
                  )}
                </div>
                <Typography size="md" className="break-words text-black-64">
                  {current.message}
                </Typography>
              </div>
              <button
                type="button"
                aria-label="Хаах"
                className="rounded-full absolute -top-1 -right-1 hover:bg-primary-soft p-1.5 outline-none transition-[background-color,box-shadow] duration-150 focus-visible:shadow-focus"
                onClick={dismiss}
              >
                <Close fill="#0A213A" className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </Transition>
    </div>
  );
};

export default Notification;
